import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import PublicNav from './PublicNav';
import PublicFooter from './PublicFooter';
import Chatbot from './chatbot/Chatbot';
import './styles/public.css';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname, hash]);
  return null;
}

export default function PublicLayout() {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  // On the home page the hero is full-screen dark, so the nav starts transparent.
  // On all other pages the content starts immediately below the nav (no dark hero),
  // so we always show the solid dark nav to keep text readable.
  const isHome = pathname === '/';

  /* ── Nav scroll state (only matters on home page) ── */
  useEffect(() => {
    if (!isHome) { setScrolled(true); return; }
    setScrolled(window.scrollY > 60);
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  /* ── Loading veil — only on first load ── */
  useEffect(() => {
    const veil = document.getElementById('site-veil');
    if (!veil) return;
    const t = setTimeout(() => veil.classList.add('gone'), 1400);
    return () => clearTimeout(t);
  }, []);

  /* ── Custom cursor ── */
  useEffect(() => {
    const ring = document.getElementById('cursor-ring');
    const dot  = document.getElementById('cursor-dot');
    if (!ring || !dot) return;

    let mx = 0, my = 0, rx = 0, ry = 0;
    const onMove = (e) => {
      mx = e.clientX; my = e.clientY;
      dot.style.left = mx + 'px'; dot.style.top = my + 'px';
    };
    document.addEventListener('mousemove', onMove);

    let rafId;
    const animCursor = () => {
      rx += (mx - rx) * 0.12; ry += (my - ry) * 0.12;
      ring.style.left = rx + 'px'; ring.style.top = ry + 'px';
      rafId = requestAnimationFrame(animCursor);
    };
    animCursor();

    const addHover = (e) => {
      if (e.target.closest('button,a,.service-card,.pkg-card,.testi-card'))
        document.body.classList.add('cursor-hover');
    };
    const removeHover = () => document.body.classList.remove('cursor-hover');
    document.addEventListener('mouseover', addHover);
    document.addEventListener('mouseout', removeHover);

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseover', addHover);
      document.removeEventListener('mouseout', removeHover);
      cancelAnimationFrame(rafId);
    };
  }, []);

  /* ── Scroll reveal — re-run on every route change so new page elements animate ── */
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add(e.target.classList.contains('fade-up') ? 'visible' : 'on');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -20px 0px' });

    // First pass immediately, second pass after React finishes painting
    const observe = () => {
      document.querySelectorAll('.fade-up:not(.visible), .rev:not(.on), .stagger:not(.on)')
        .forEach((el) => io.observe(el));
    };
    observe();
    const t1 = setTimeout(observe, 80);
    const t2 = setTimeout(observe, 350); // catch any late-rendering components
    return () => { clearTimeout(t1); clearTimeout(t2); io.disconnect(); };
  }, [pathname]); // re-run whenever the route changes

  return (
    <div className="public-site">
      {/* Loading veil — only shown once on very first page load */}
      <div id="site-veil"><div className="veil-word">KAYASPARSHA</div></div>

      {/* Custom cursor */}
      <div id="cursor-ring" />
      <div id="cursor-dot" />

      <ScrollToTop />
      <PublicNav scrolled={scrolled} />
      <main>
        <Outlet />
      </main>
      <PublicFooter />
      <Chatbot />
    </div>
  );
}
